import { Button, CircularProgress, Stack, Typography } from '@mui/material'
import { type FC, useCallback, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import useToast from 'shared/components/Toast/useToast'
import { getToken } from 'shared/utils/auth'
import config from '../../../config'

type LinkState = 'confirm' | 'linking' | 'success' | 'error'

// Page shown when a recruiter clicks the Slack account link URL.
// The code query param is a signed JWT from the bot containing the Slack user/team IDs.
const SlackLink: FC = () => {
    const [searchParams] = useSearchParams()
    const code = searchParams.get('code')
    const [state, setState] = useState<LinkState>(code ? 'confirm' : 'error')
    const [userName, setUserName] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const toast = useToast()

    const handleLink = useCallback(async () => {
        setState('linking')
        try {
            const token = getToken()
            const res = await fetch(`${config.api.uri}/bot/auth/link`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ code }),
            })

            const data = await res.json()

            if (!res.ok) {
                setState('error')
                setErrorMessage(data.error || 'Something went wrong')
                return
            }

            setUserName(data.atsUserName)
            setState('success')
            toast({
                title: 'Linked',
                message: 'Your Slack account is now linked to CPI.',
                severity: 'success',
            })
        } catch {
            setState('error')
            setErrorMessage(
                'Unable to connect to the server. Please try again.'
            )
        }
    }, [code, toast])

    if (!code) {
        return (
            <Stack
                alignItems="center"
                justifyContent="center"
                height="60vh"
                gap={2}
            >
                <Typography variant="h4">Invalid Link</Typography>
                <Typography color="text.secondary">
                    This link is missing or expired. Ask the bot for a new one.
                </Typography>
            </Stack>
        )
    }

    return (
        <Stack
            alignItems="center"
            justifyContent="center"
            height="60vh"
            gap={3}
        >
            {state === 'confirm' && (
                <>
                    <Typography variant="h4">
                        Link your Slack account
                    </Typography>
                    <Typography color="text.secondary">
                        This will connect your Slack identity to your CPI
                        recruiter account.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleLink}
                        data-testid="slack-link-confirm-btn"
                    >
                        Confirm Link
                    </Button>
                </>
            )}

            {state === 'linking' && (
                <>
                    <CircularProgress />
                    <Typography color="text.secondary">
                        Linking your account...
                    </Typography>
                </>
            )}

            {state === 'success' && (
                <>
                    <Typography variant="h4">
                        You're linked, {userName}!
                    </Typography>
                    <Typography color="text.secondary">
                        You can close this tab and go back to Slack.
                    </Typography>
                </>
            )}

            {state === 'error' && (
                <>
                    <Typography variant="h4">Something went wrong</Typography>
                    <Typography color="text.secondary">
                        {errorMessage ||
                            'The link may have expired. Ask the bot for a new one.'}
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => setState('confirm')}
                        data-testid="slack-link-retry-btn"
                    >
                        Try Again
                    </Button>
                </>
            )}
        </Stack>
    )
}

export default SlackLink
